import * as moment from 'moment-timezone';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, getFrontendDomain } from '../api/auth.service';
import { ITranslation } from '../api/models/db/translations.schema';
import { RoutesService } from '../api/routes.service';
import { WhitelabelService } from '../domain/whitelabel.service';
import { IS_LIVE_TRANSLATIONS_FEATURE_ENABLED } from '../domain/contexts/mpt';

const LS_TOGGLE_PULL_LIVE_TRANS = 'pull-live-trans';
const LS_STORE_DATA = 'trans-cache';
const LS_S3_SNAPSHOT_TIMESTAMP = 'trans-s3-timestamp';
export const LS_UPDATE_PENDING = 'trans-update-pending';
export const LS_SNAPSHOT_PENDING = 'trans-snapshot-pending';

interface ITranslationSnapshotRes {
  url: string;
  version: string;
  id?: number;
}


@Injectable({
  providedIn: 'root'
})
export class LangService {

  private supportedLanguages = ['en', 'fr'];
  private knownLanguages = {
    'en': 'lbl_en',
    'fr': 'lbl_fr',
  }
  private currentLangCode:string = 'en';
  // private TRA_DB =  require('../../../data/translations.json');
  private TRA_DB =  {};
  private currentUrl:string = ''
  private isLiveTrans:boolean = false;
  private isInitialized: boolean = false;

  constructor(
    private auth:AuthService,
    private routes:RoutesService,
    private router:Router,
    private whitelabel: WhitelabelService,
  ) { 
    this.isLiveTrans = window.localStorage.getItem(LS_TOGGLE_PULL_LIVE_TRANS) === 'yes';
  }
  
  async initializeTranslations() {
    if (this.isInitialized) {
      return; // Already initialized
    }
    try {
      if (this.isLiveTrans) {
        await this.loadTranslationMapFromDb();
      }
      else {
        if (this.whitelabel.getSiteFlag(IS_LIVE_TRANSLATIONS_FEATURE_ENABLED)) {
          // Try to load from S3 snapshot first, then fallback to local
          if (!this.TRA_DB || Object.keys(this.TRA_DB).length === 0) {
            await this.loadTranslationMapFromS3();
            window.localStorage.removeItem(LS_STORE_DATA);
          }
        } else {
          window.localStorage.removeItem(LS_STORE_DATA);
          this.loadLocalFallbackTranslations();
        }
      }
    }
    catch(error) {
      console.error('Error initializing translations:', error);
      // Fallback to local translations if S3 load fails
      console.log('loading local fallback translations');
      this.loadLocalFallbackTranslations();
    }
    this.isInitialized = true;
  }
  
  private loadLocalFallbackTranslations() {
    try {
      this.TRA_DB = require('../../../data/translations.json');
      console.log("Loaded local fallback translations");
    } catch (error) {
      console.error("Failed to load local fallback translations:", error);
      this.TRA_DB = {}; // Ensure TRA_DB is defined even if local load fails
    }
  }

  updateCurrentUrl(url:string){
    this.currentUrl = url;
  }

  getKnownLangName(langCode:string){
    return this.knownLanguages[langCode] || langCode;
  }
  
  getKnownLanguages() {
    return this.knownLanguages;
  }

  directDownloadTranslationMapFromDb(){
    return this.auth
      .apiFind(this.routes.TRANSLATION, {
        query: {
          $limit: 6000, // for all
        }
      })
      .then(activeRecords => {
        return this.createTranslationMap( activeRecords.allTransRecs.data );
      });
  }

  async loadTranslationMapFromDb(){
    const cachedTranslations = window.localStorage.getItem(LS_STORE_DATA);
    if (cachedTranslations){
      this.mapAndUpdateCachedTranslation(cachedTranslations);
    }
    // Load DB snapshot if not loaded or an update was done
    if (!cachedTranslations || window.localStorage.getItem(LS_UPDATE_PENDING) === 'true'
      ) {
      try {
        const translationsFromDb = await this.getTranslationsFromDb();
        if (!translationsFromDb) {
          console.error('No translations available from DB'); 
          throw new Error('No translations available from DB');
        }
        window.localStorage.setItem(LS_STORE_DATA, translationsFromDb);
        // AND update right away, or reload?
        this.mapAndUpdateCachedTranslation(translationsFromDb);
        window.localStorage.setItem(LS_UPDATE_PENDING, 'false');
        // reload shows better perf and clearner network but because it reloads
        // setTimeout(()=>{
        //   window.location.reload();
        // });
      } catch (error) {
        console.error('Error loading translations from DB:', error);
      }
    }
  }
  
  mapAndUpdateCachedTranslation(translation: string) {
    const map = this.createTranslationMap( JSON.parse(translation));
    this.overrideTranslationsDatabase(map);
  }
  
  async getCurrentDBTranslationState(): Promise<{data: ITranslation[], [key: string]: any}> {
    const activeRecords = await this.auth
      .apiFind(this.routes.TRANSLATION, {
        query: {
          $limit: 6000, // 6000 for all
          isLiveTrans: this.isLiveTrans ? true : undefined, // only pull live translations if the toggle is on
          slug: true,
          en: true,
          fr: true
        }
      })
    return activeRecords;
  }
  
  async getTranslationsFromDb(): Promise<string | null> {
    try {
      // const cachedTranslations = cachedTranslation || window.localStorage.getItem(LS_STORE_DATA);
      
      const currentTranslations = await this.getCurrentDBTranslationState();
      
      if (!currentTranslations || !currentTranslations.allTransRecs.data) {
        console.warn('No current translations available');
        return null;
      }
      
      const newCachedTranslations = JSON.stringify(currentTranslations.allTransRecs.data);
      return newCachedTranslations;
      
    } catch (error) {
      console.error("Error comparing translations:", error);
      return null;
    }
  }

  createTranslationMap(records:ITranslation[]){
    const map:any = {};
    const langs = this.supportedLanguages;
    langs.forEach(lang => {
      map[lang] = {};
    });
    records.forEach(record =>{
      const key = record.slug || record.id;
      langs.forEach(lang => {
        map[lang][key] = record[lang];
      })
    })
    return map;
  }

  getSupportedLanguages(){
    return this.supportedLanguages;
  }

  isUsingLiveTranslations(){
    return this.isLiveTrans; 
  }
  
  // Previous version - just de-activating
  deactivateLiveTranslations(){
    this.isLiveTrans = false;
    window.localStorage.removeItem(LS_TOGGLE_PULL_LIVE_TRANS);
    setTimeout(()=>{
      window.location.reload();
    })
  }

  /**
   * When de-activating live translation mode, take a snapshot of current translations table and store version
   */
  // async deactivateLiveTranslations(){
  //   this.isLoadingNewMode = true;
  //   window.localStorage.removeItem(LS_TOGGLE_PULL_LIVE_TRANS);
  //   try {
  //     if (window.localStorage.getItem(LS_SNAPSHOT_PENDING) === 'false') {
  //       console.log('No update - no need for new snapshot');
  //     } else {
  //       const res = await this.auth.apiUpdate(this.routes.TRANSLATION, 0, 
  //       { langs: this.supportedLanguages, context: this.whitelabel.contextData.id, domain: getFrontendDomain() },
  //       { query: { isSnapshot: true } });
  //       const {success, version} = res;
  //       if (success && version) {
  //         window.localStorage.setItem(LS_S3_SNAPSHOT_TIMESTAMP, version);
  //         this.isLoadingNewMode = false;
          
  //       }
  //       // window.localStorage.setItem(LS_UPDATE_PENDING, 'false');
  //       setTimeout(()=>{
  //         window.location.reload();
  //       })
  //     }
  //   } catch (error) {
  //     console.error('Error updating translations:', error);
  //   } finally {
  //     this.isLoadingNewMode = false;
  //     this.isLiveTrans = false;
  //     window.localStorage.removeItem(LS_UPDATE_PENDING);
  //     window.localStorage.removeItem(LS_SNAPSHOT_PENDING);
  //   }
  // }

  /**
   * Take a snapshot of current translations table,  store version in bucket and update cache
   */
  async takeTranslationSnapshot() {
    this.isLoadingNewMode = true;
    try {
      if (!window.localStorage.getItem(LS_SNAPSHOT_PENDING) || window.localStorage.getItem(LS_SNAPSHOT_PENDING) === 'false') {
        console.log('No update - no need for new snapshot');
        return;
      } else {
        const res = await this.auth.apiUpdate(this.routes.TRANSLATION, 0, 
        { langs: this.supportedLanguages, context: this.whitelabel.contextData.id, domain: getFrontendDomain() },
        { query: { isSnapshot: true } });
        const {success, version, data} = res;
        if (success && version && data) {
          window.localStorage.setItem(LS_S3_SNAPSHOT_TIMESTAMP, version);
          window.localStorage.setItem(LS_STORE_DATA, JSON.stringify(data));
        }
        setTimeout(()=>{
          window.location.reload();
        })
      }
    } catch (error) {
      console.error('Error updating translations:', error);
    } finally {
      this.isLoadingNewMode = false;
      window.localStorage.removeItem(LS_UPDATE_PENDING);
      window.localStorage.removeItem(LS_SNAPSHOT_PENDING);
    }
  }
  
  /**
   * Load translation data from S3 snapshot
   * Falls back to local file if S3 load fails
   */
  private async loadTranslationMapFromS3(): Promise<void> {
    try {
      const context = this.whitelabel.contextData?.id || 'MPT';
      const cachedTimestamp = window.localStorage.getItem(LS_S3_SNAPSHOT_TIMESTAMP);
      
      // If we have a cached timestamp, try to load that specific snapshot
      let translationUrl: ITranslationSnapshotRes;
      // To define how we really want to handle which version vs latest
      if (cachedTimestamp) {
        translationUrl = await this.getTranslationSnapshotUrl(cachedTimestamp);
      } else {
        translationUrl = await this.getTranslationSnapshotUrl(undefined, true);
      }
      
      const translationData = await this.fetchTranslationData(translationUrl.url, translationUrl.version);
      
      if (translationData) {
        this.overrideTranslationsDatabase(translationData);
        console.log("Successfully loaded translations from S3");
      }
      
    } catch (error) {
      console.warn("Failed to load translations from S3, using local fallback:", error);
      this.TRA_DB = require('../../../data/translations.json');
    }
  }
  
  async getTranslationSnapshotUrl(timestamp: string | undefined, isLatest?: boolean): Promise<ITranslationSnapshotRes> {
    let response: ITranslationSnapshotRes;
    try {
      const query = timestamp ?
        { query: { timestamp } } :
        isLatest ? { query: { isLatest: true } } : 
        undefined;
        
      response = await this.auth.apiGet(this.routes.TRANSLATION_SNAPSHOT, 0, query);
      
      return response;
    } catch (error) {
      console.error("Error fetching translation snapshot URL:", error);
      if (timestamp) {
        window.localStorage.removeItem(LS_S3_SNAPSHOT_TIMESTAMP); // clear the timestamp if fetch fails
      }
      throw error;
    }
  }
  
  async fetchTranslationData(url: string, version: string): Promise<any> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch translation data: ${response.statusText}`);
      }
      if (response.ok) {
        const data = await response.json();
        if (version) {
          window.localStorage.setItem(LS_S3_SNAPSHOT_TIMESTAMP, version);
        }
        return data;
      } else {
        console.error('Failed to fetch translation snapshot:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching translation data:', error);
      throw error;
    }
  }
  
  isLoadingNewMode: boolean = false;
  async activateLiveTranslations(){
    this.isLoadingNewMode = true;
    this.isLiveTrans = true;
    window.localStorage.setItem(LS_TOGGLE_PULL_LIVE_TRANS, 'yes')
    window.localStorage.setItem(LS_UPDATE_PENDING, 'false');
    window.localStorage.setItem(LS_SNAPSHOT_PENDING, 'false');
    await this.loadTranslationMapFromDb();
    this.isLoadingNewMode = false;
  }

  c() {
    return this.getCurrentLanguage();  
  }
  getCurrentLanguage(){
    return this.currentLangCode;
  }

  setCurrentLanguage(newLangCode:string){
    if (this.supportedLanguages.indexOf(newLangCode) === -1){
      console.warn(`Provided language not supported, using English instead. (${newLangCode})`);
    }
    else{
      this.currentLangCode = newLangCode;
    }
    this.updateMomentLocale();
  }

  updateMomentLocale(){
    if (this.currentLangCode === 'fr'){
      moment.locale('fr');
    }
    else{
      moment.locale('en');
    }
  }

  loadInLang(newLangCode:string){
    // TO DO : would rather have this done dynamically by swapping in and out of the route params, but this also works under the strict structure that I have in place
    const replacementPattern = '/'+ this.currentLangCode +'/';
    const routePath = this.currentUrl.split('/');
    if (routePath[1] === this.currentLangCode){
      routePath[1] = newLangCode;
    }
    const cleanRoute = [];
    routePath.forEach(part => {
      cleanRoute.push(decodeURI(part))
    })
    this.router
      .navigate(cleanRoute)
      .then(v => {
        window.location.reload(); // force refresh...
      })
  }

  traWithWhitelabel(whitelabelSiteTextKey:string, slug:string | number, langOverride?:string, props?:{[key:string]: string | number}){
    if (whitelabelSiteTextKey){
      const wlSlug = this.whitelabel.getSiteText(whitelabelSiteTextKey);
      if (wlSlug){ slug = wlSlug }
    }
    return this.tra(slug, langOverride, props);
  }  


  tra(key:string | number, langOverride?:string, props?:{[key:string]: string | number} ): string{
    if (!key){ return ''; }
    key = (''+key).trim()
    const lang = langOverride || this.currentLangCode;
    let str = this.TRA_DB[lang][key] || key;
    if(props) {
      str = str.replace(/{{([^}}]+)?}}/g, ($1, $2) => 
      $2.split('.').reduce((p, c) => p ? p[c] : '', props));
    }
    // colon 
    str = str.split(' :').join(String.fromCharCode(160)+':');
    return str;
  }


  traWithParam(key: string | number, param?: any, langOverride?: string) {
    if (!key) { return ''; }
    key = ('' + key).trim();
    const lang = langOverride || this.currentLangCode;
    return param ? `${this.TRA_DB[lang][key]} ${param}` : this.TRA_DB[lang][key];
  }

  traSrc(src:{[key:string]: string}){
    const lang = this.currentLangCode;
    return src[lang] || src[Object.keys(src)[0]]; // loose defaul
  }

  overrideTranslationsDatabase(data){
    this.TRA_DB = data;
  }
  
  popupFrenchLangWIP(){
    alert('Ce site est en chantier et n\'est pas encore disponible en français.')
  }

  popupWIP(slug:string){
    if (slug){
      alert(this.tra(slug))
    }
    else{
      alert('This link is not currently available.')
    }
  }
  
}
